import { VARIABLE_GROUPS } from "./template.constants";

// Pure helpers of the Template tab (no API, no React): the channel's template_schema -> form fields, and the editor
// values / payload.

/** The content fields a channel's template_schema describes, in its `order` (required first when unset). */
export function schemaFields(schema) {
  const required = new Set(schema?.required ?? []);
  return Object.entries(schema?.properties ?? {})
    .map(([key, prop]) => ({ key, prop: prop ?? {}, required: required.has(key) }))
    .sort((a, b) => (a.prop.order ?? 99) - (b.prop.order ?? 99) || Number(b.required) - Number(a.required) || a.key.localeCompare(b.key));
}

/** An empty content object for a schema; fields of `keep` that the schema also has are carried over. */
export function emptyContent(schema, keep = {}) {
  return Object.fromEntries(
    schemaFields(schema).map(({ key, prop }) => [key, keep?.[key] !== undefined ? keep[key] : prop.type === "array" ? [] : prop.default !== undefined ? prop.default : ""])
  );
}

/** Drops optional fields left empty and fields the schema does not list, so the server checks what was written. */
export function cleanContent(content, schema) {
  const props = schema?.properties ?? {};
  const required = new Set(schema?.required ?? []);
  const out = {};
  for (const [key, value] of Object.entries(content ?? {})) {
    if (!props[key]) continue;
    const empty = value === "" || value === null || value === undefined || (Array.isArray(value) && !value.length);
    if (!empty || required.has(key)) out[key] = value;
  }
  return out;
}

/** Required fields left empty, as messages. */
export const missingRequired = (content, schema, label) =>
  (schema?.required ?? [])
    .filter((key) => {
      const value = content?.[key];
      return value === undefined || value === null || (typeof value === "string" && !value.trim());
    })
    .map((key) => `${label}: ${schema?.properties?.[key]?.title ?? key} is required.`);

/** Fields longer than their maxLength, as messages. */
export const tooLong = (content, schema, label) =>
  schemaFields(schema)
    .filter(({ key, prop }) => prop.maxLength && typeof content?.[key] === "string" && content[key].length > prop.maxLength)
    .map(({ key, prop }) => `${label}: ${prop.title ?? key} must be at most ${prop.maxLength} characters.`);

/** Variables grouped for the chip list, in a stable group order. */
export function groupVariables(variables) {
  const groups = new Map();
  for (const variable of variables ?? []) {
    const group = variable.group ?? "Other";
    groups.set(group, [...(groups.get(group) ?? []), variable]);
  }
  const rank = (group) => (VARIABLE_GROUPS.indexOf(group) === -1 ? 99 : VARIABLE_GROUPS.indexOf(group));
  return [...groups.entries()].sort(([a], [b]) => rank(a) - rank(b));
}

export const toFormValues = (record) => ({
  code: record?.code ?? "",
  name: record?.name ?? "",
  channel_fid: record?.channel_fid ?? null,
  trigger_fid: record?.trigger_fid ?? null,
  locale: record?.locale ?? "en",
  content: record?.content ?? {},
  digest_content: record?.digest_content ?? null,
  is_activated: record ? record.is_activated !== false : true,
});

export const toPayload = (values, schema, supportsDigest) => ({
  code: values.code.trim().toUpperCase(),
  name: values.name.trim(),
  channel_fid: values.channel_fid,
  trigger_fid: values.trigger_fid || null,
  locale: values.locale.trim(),
  content: cleanContent(values.content, schema),
  digest_content: supportsDigest && values.digest_content ? cleanContent(values.digest_content, schema) : null,
  is_activated: values.is_activated,
});
